import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { GetClaimFeature, type ApiGetClaimFeatureResponse } from '@/infrastructure/api-functions/core-search-api/get-claim-feature-api';
import { GetClaimCompareMatch } from '@/infrastructure/api-functions/core-search-api/get-claim-compare-match-api';
import { GetResultPosition } from '@/infrastructure/api-functions/core-search-api/get-result-position-api';
import { GetClaimCompareTranslation } from '@/infrastructure/api-functions/core-search-api/get-claim-compare-translation-api';
import _ from 'lodash';
import type { GetClaimFeatureRepository, GetClaimCompareMatchRepository, GetResultPositionRepository, GetClaimCompareTranslationRepository, SetClaimCompareTranslationCacheRepository, GetClaimCompareExportRepository, GetClaimCompareResultRepository, GetComparePatentRepository } from '../../services/claim-charting-repository';
import type { ClaimFeatureData, GetClaimCompareMatchParams, ClaimCompareMatchData, GetResultPositionParams, ResultPositionData, GetClaimCompareTranslationParams, ClaimCompareTranslationData, GetClaimCompareExportParams, SetClaimCompareTranslationCacheParams, CompareFeatureData, FeatureCompareState, GetComparePatentData } from '../../types';
import { SetClaimCompareTranslationCache } from '@/infrastructure/api-functions/core-search-api/get-claim-compare-translation-cache-api';
import { GetClaimCompareExport } from '@/infrastructure/api-functions/core-search-api/get-claim-compare-export-api';
import { GetClaimCompareResult, type ApiGetClaimCompareResultRequest, type ApiGetClaimCompareResultResponse } from '@/infrastructure/api-functions/core-search-api/get-claim-compare-result-api';
import type { SSEResponse, PsHttpSSE } from '@patsnap-utils/sse';
import type { StreamStatus } from '../../types/enum';
import { SearchComparePatent } from '@/infrastructure/api-functions/core-search-api/core-search-api';
@injectable()
export class ClaimChartingRepositoryImpl implements
    GetClaimFeatureRepository,
    GetClaimCompareMatchRepository,
    GetResultPositionRepository,
    GetClaimCompareTranslationRepository,
    GetClaimCompareExportRepository,
    SetClaimCompareTranslationCacheRepository,
    GetClaimCompareResultRepository,
    GetComparePatentRepository
{
    private _streamClaim: PsHttpSSE<ApiGetClaimFeatureResponse> | null = null;
    private _streamCompare: PsHttpSSE<ApiGetClaimCompareResultResponse> | null = null;
    @inject(GetClaimFeature)
    private _getClaimFeature!: GetClaimFeature;
    @inject(GetClaimCompareMatch)
    private _getClaimCompareMatch!: GetClaimCompareMatch;
    @inject(GetResultPosition)
    private _getResultPosition!: GetResultPosition;
    @inject(GetClaimCompareTranslation)
    private _getClaimCompareTranslation!: GetClaimCompareTranslation;
    @inject(GetClaimCompareExport)
    private _getClaimCompareExport!: GetClaimCompareExport;
    @inject(SetClaimCompareTranslationCache)
    private _setClaimCompareTranslationCache!: SetClaimCompareTranslationCache;
    @inject (GetClaimCompareResult)
    private _getClaimCompareResult!: GetClaimCompareResult;
    @inject (SearchComparePatent)
    private _searchComparePatent!: SearchComparePatent;
    constructor() {
    }

    getClaimFeatures(
        patentId: string,
        callback: (status: StreamStatus, content?: ClaimFeatureData) => void,
        errorCallback?: (error: { error_code?: number }) => void,
    ): PsHttpSSE<ApiGetClaimFeatureResponse> {
        // V2 接口：使用 type='patent' 调用新的 POST 接口
        this._streamClaim = this._getClaimFeature.getClaimFeature({
            type: 'patent',
            patent_id: patentId,  // 使用 snake_case，后端 Spring Boot 会自动转换为 camelCase
        })
            .onMessage((data: SSEResponse<ApiGetClaimFeatureResponse & { error_code?: number }>) => {
                const { status, content, error_code } = data.data ?? {};

                if (!status && error_code === 11120121) {
                    errorCallback?.({ error_code: 11120121 });
                }

                if (content) {
                    const getData = () => {
                        const claims = content.separate_claims.map(item => {
                            return {
                                name: item.name,
                                isIndep: item.indep_claim, // indep_claim：true时是主权
                                features: item.separates.map(feature => ({
                                    value: feature,
                                    trans: undefined,  // 初始不翻译，点击翻译按钮后才设置翻译内容
                                    id: _.uniqueId(),
                                })),
                            };
                        });

                        return {
                            claims,
                            patentSN: content.pn || '',
                            claimLang: content.claim_lang,
                        };
                    };
                    callback(status, getData());
                } else {
                    callback(status);
                }
            })
            .onError((error: { error_code?: number }) => {
                errorCallback?.(error);
            });

        this._streamClaim && this._streamClaim.start();
        return this._streamClaim;
    }

    getClaimFeaturesFromText(
        text: string,
        callback: (status: StreamStatus, content?: ClaimFeatureData) => void,
        errorCallback?: (error: { error_code?: number }) => void,
    ): PsHttpSSE<ApiGetClaimFeatureResponse> {
        // V2 接口：使用 type='freetext' 调用新的 POST 接口
        this._streamClaim = this._getClaimFeature.getClaimFeature({
            type: 'freetext',
            content: text,
        })
            .onMessage((data: SSEResponse<ApiGetClaimFeatureResponse & { error_code?: number }>) => {
                const { status, content, error_code } = data.data ?? {};

                if (!status && error_code === 11120121) {
                    errorCallback?.({ error_code: 11120121 });
                }

                if (content) {
                    const getData = () => {
                        const claims = content.separate_claims.map(item => {
                            return {
                                name: item.name,
                                isIndep: item.indep_claim,
                                features: item.separates.map(feature => ({
                                    value: feature,
                                    trans: undefined,
                                    id: _.uniqueId(),
                                })),
                            };
                        });

                        const result = {
                            claims,
                            patentSN: 'Free Text',
                            claimLang: content.claim_lang,
                        };
                        return result;
                    };
                    callback(status, getData());
                } else {
                    callback(status);
                }
            })
            .onError((error: { error_code?: number }) => {
                errorCallback?.(error);
            });

        this._streamClaim && this._streamClaim.start();
        return this._streamClaim;
    }

    async getClaimCompareMatch(data: GetClaimCompareMatchParams): Promise<ClaimCompareMatchData> {
        const res = await this._getClaimCompareMatch.getClaimCompareMatch({
            source_text: data.sourceText,
            source_patent: data.sourcePatent,
            source_lang: data.sourceLang,
            target_type: data.targetType,
            file_type: data.fileType || 'patent',  // 默认为 'patent'（向后兼容）
        });
        return {
            matchText: res.match_text?.[0] || '',
        };
    }

    async getResultPosition(data: GetResultPositionParams): Promise<ResultPositionData> {
        const res = await this._getResultPosition.getResultPosition({
            // V1 风格参数（恢复）
            source_text: data.sourceText,
            source_patent: data.sourcePatent,
            source_lang: data.sourceLang,
            target_type: data.targetType,

            // V2 扩展
            file_type: data.fileType,
            origin_text: data.originText,
        });

        // 清理原文中的生物高亮标签
        const cleanedOriginalText = this._cleanBioHighlightTags(res.original_text || '');

        return {
            matchText: res.match_text || [],  // 返回完整数组，符合接口定义
            originalText: cleanedOriginalText,
        };
    }

    /**
     * 清理原文中的生物高亮标签
     * 移除 <a class="bio-hl"> 标签，保留内部文本
     */
    private _cleanBioHighlightTags(html: string): string {
        if (!html) return html;
        return html.replace(/<a\s+[^>]*class="[^"]*bio-hl[^"]*"[^>]*>([\s\S]*?)<\/a>/gi, '$1');
    }

    getClaimCompareTranslation(data: GetClaimCompareTranslationParams): Promise<ClaimCompareTranslationData> {
        return this._getClaimCompareTranslation.getClaimCompareTranslation({
            patent_id: data.patentId,
            source_lang: data.sourceLang || '',  // patent 类型有值，text 类型传空字符串
            trans_lang: data.transLang,
            text: data.text,
        });
    }

    getClaimCompareExport(data: GetClaimCompareExportParams): Promise<string> {
        return this._getClaimCompareExport.getClaimCompareExport({
            ai_flag: data.ai_flag,
            file_name: data.fileName,
            charts: data.charts.map(ele => ({
                sheet_name: ele.sheetName,
                head: ele.head,
                body: ele.body,
            })),
        });
    }

    setClaimCompareTranslationCache(data: SetClaimCompareTranslationCacheParams): Promise<ClaimCompareTranslationData>{
        return this._setClaimCompareTranslationCache.setClaimCompareTranslationCache({
            patent_id: data.patentId,
            translation: data.translation,
            trans_lang: data.transLang,
            text: data.text,
        });
    }

    _createClaimCompareParams(params): ApiGetClaimCompareResultRequest {
        // 优先使用 V2 参数，fallback 到 V1 参数（向后兼容）
        const paramsData: ApiGetClaimCompareResultRequest = {
            // V2 参数（使用 snake_case）
            target_type: params.targetType,
            target_patent_id: params.targetPatentId,
            target_content: params.targetContent,
            file_type: params.fileType,
            file_patent_id: params.filePatentId,
            file_content: params.fileContent,

            // V1 参数（向后兼容）
            claim_patent: params.claimPatentId,
            desc_patent: params.descPatentId,
            claim_lang: params.claimLang,

            // 通用参数
            separate_claims: params.compares.map(compare => {
                const separates = compare.compareFeatures.map(feature => feature.claimVal);
                return { name: compare.name, separates };
            }),
        };
        return paramsData;
    }

    getClaimCompareResult(
        params: FeatureCompareState,
        callback: (streamStatus: StreamStatus, content?: Partial<CompareFeatureData>) => void,
        errorCallback?: (error: { error_code?: number }) => void,
    ): PsHttpSSE<ApiGetClaimCompareResultResponse> {
        const paramsData: ApiGetClaimCompareResultRequest = this._createClaimCompareParams(params);

        // 保存原始 ID（按 claim 结构保存，而不是扁平化）
        // 使用二维数组：originalIds[claimIndex][featureIndex] = id
        const originalIds: string[][] = params.compares.map(compare =>
            compare.compareFeatures.map(f => f.id),
        );

        this._streamCompare = this._getClaimCompareResult.getClaimCompareResult(paramsData)
            .onMessage((data: SSEResponse<ApiGetClaimCompareResultResponse & { error_code?: number }>) => {
                const { status, content, error_code } = data.data ?? {};

                if (!status && error_code === 11120122) {
                    errorCallback?.({ error_code: 11120122 });
                }
                if (content) {
                    const compares = content.separate_compares.map((item, claimIndex) => {
                        // 获取当前 claim 的原始 ID 数组
                        const claimOriginalIds = originalIds[claimIndex] || [];

                        return {
                            name: item.name,
                            isIndep: item.indep_claim,
                            compareFeatures: item.separate_compare.map((feature, featureIndex) => ({
                                claimVal: feature.claim_feature,
                                // 使用二维索引：originalIds[claimIndex][featureIndex]
                                id: claimOriginalIds[featureIndex] || _.uniqueId(),
                                compareVal: feature.compare_feature,
                                isPublic: feature.is_public,
                                reason: feature.reason,
                            })),
                        };
                    });

                    const resultData = {
                        descLang: content.desc_lang,
                        compares,
                    };
                    callback(status, resultData);
                } else {
                    callback(status);
                }
            })
            .onError((error) => {
                errorCallback?.(error);
            });
        this._streamCompare && this._streamCompare.start();
        return this._streamCompare;
    }
    async getComparePatent(q: string): Promise<GetComparePatentData> {
        const res = await this._searchComparePatent.searchComparePatent({
            q,
            simple: true,
        });

        // 处理空数据情况，防止无限循环
        if (!res || !res.patent_info) {
            return {
                patentInfo: undefined,
                isNoData: true,
            };
        }

        return {
            patentInfo: res.patent_info,
            isNoData: false,
        };
    }
}
